import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Phone } from "lucide-react";

interface CrmSummary {
  total_with_crm: number;
  total_students: number;
  crm_coverage_percent: number;
  by_status: {
    renewed: number;
    will_renew: number;
    undecided: number;
    not_renewing: number;
  };
}

interface CrmSummaryCardProps {
  crmSummary: CrmSummary | undefined;
  isLoading: boolean;
}

export function CrmSummaryCard({ crmSummary, isLoading }: CrmSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4" />
            สถิติการโทร CRM
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-2 w-full" />
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!crmSummary) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Phone className="h-4 w-4" />
          สถิติการโทร CRM
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Coverage Progress */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">โทรติดตามแล้ว</span>
            <span className="font-medium">
              {crmSummary.total_with_crm}/{crmSummary.total_students} คน ({crmSummary.crm_coverage_percent}%)
            </span>
          </div>
          <Progress value={crmSummary.crm_coverage_percent} className="h-2" />
        </div>

        {/* Breakdown by status */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-green-50 dark:bg-green-950/30">
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200">
              ✅ ต่อแล้ว
            </Badge>
            <span className="font-semibold text-green-700 dark:text-green-300">
              {crmSummary.by_status.renewed}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-blue-50 dark:bg-blue-950/30">
            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200">
              🔵 ต้องการต่อ
            </Badge>
            <span className="font-semibold text-blue-700 dark:text-blue-300">
              {crmSummary.by_status.will_renew}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-yellow-50 dark:bg-yellow-950/30">
            <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200">
              🟡 ยังไม่ตัดสินใจ
            </Badge>
            <span className="font-semibold text-yellow-700 dark:text-yellow-300">
              {crmSummary.by_status.undecided}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30">
            <Badge variant="destructive">
              🔴 ไม่ต้องการต่อ
            </Badge>
            <span className="font-semibold text-red-700 dark:text-red-300">
              {crmSummary.by_status.not_renewing}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
